import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Star, Zap, Building } from 'lucide-react'
import { usePlans } from '../../hooks/usePlans'
import { redirectToCheckout } from '../../services/stripeService'
import { useAuth } from '../../contexts/AuthContext'
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll'
import toast from 'react-hot-toast'
import CustomPlanCard from '../billing/CustomPlanCard'

interface PricingSectionProps {
  highlight?: 'premium' | 'escolar'
}

const PricingSection: React.FC<PricingSectionProps> = ({ highlight = 'premium' }) => {
  const { plans, isLoading: plansLoading } = usePlans({ isActive: true })
  const [isAnnual, setIsAnnual] = useState(false)
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null)
  const { user } = useAuth()
  const navigate = useNavigate()
  // Antes dos early returns (regras de hooks); o ref só é usado no return principal.
  const revealRef = useRevealOnScroll<HTMLDivElement>()

  // Memoize filtered and ordered plans to prevent infinite re-renders
  const orderedPlans = useMemo(() => {
    if (!plans) return []

    // Filter plans by duration and exclude free plans
    const filteredPlans = plans.filter(plan => {
      const matchesDuration = isAnnual ? plan.duration_months === 12 : plan.duration_months === 1
      const isNotFree = !plan.name.toLowerCase().includes('gratuito') &&
                        !plan.name.toLowerCase().includes('free') &&
                        !plan.name.toLowerCase().includes('grátis')
      return matchesDuration && isNotFree
    })

    // Define the desired order for plan display based on highlight prop
    const planOrder = highlight === 'escolar'
      ? ['Escolar', 'Premium']
      : ['Premium', 'Escolar']

    // Sort plans according to the desired order
    const sorted = filteredPlans.sort((a, b) => {
      const aBaseName = a.name.replace(' Anual', '')
      const bBaseName = b.name.replace(' Anual', '')
      const aIndex = planOrder.indexOf(aBaseName)
      const bIndex = planOrder.indexOf(bBaseName)

      // If plan is not in the order array, put it at the end
      if (aIndex === -1) return 1
      if (bIndex === -1) return -1

      return aIndex - bIndex
    })

    return sorted
  }, [plans, isAnnual, highlight])

  // Handle subscription checkout
  const handleSubscribe = async (plan: any, baseName: string) => {
    // If user is not logged in, redirect to register
    if (!user) {
      navigate('/register')
      return
    }

    // Check if plan has a valid Stripe price ID
    if (!plan.stripe_price_id) {
      toast.error('Este plano ainda não está disponível. Entre em contato conosco.')
      return
    }

    // Redirect to Stripe checkout with trial
    setLoadingPriceId(plan.stripe_price_id)
    try {
      await redirectToCheckout(plan.stripe_price_id)
    } catch (error) {
      toast.error('Erro ao processar pagamento. Tente novamente.')
    } finally {
      setLoadingPriceId(null)
    }
  }

  // Memoize calculate savings function
  const calculateSavings = useCallback((annualPlan: any) => {
    if (!plans || annualPlan.duration_months !== 12) return null

    const baseName = annualPlan.name.replace(' Anual', '')
    const monthlyPlan = plans.find(p => p.name === baseName && p.duration_months === 1)

    if (!monthlyPlan) return null

    const monthlyTotal = parseFloat(monthlyPlan.price) * 12
    const annualPrice = parseFloat(annualPlan.price)
    const savings = monthlyTotal - annualPrice
    const percentage = Math.round((savings / monthlyTotal) * 100)

    return { savings, percentage, monthlyTotal }
  }, [plans])

  if (plansLoading) {
    return (
      <section className="py-20 bg-surface dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display text-ink-900 dark:text-cream-50 mb-4">
              Comece grátis. <span className="italic font-normal text-terra-500">Continue se fizer sentido.</span>
            </h2>
            <p className="text-xl text-ink-500 dark:text-cream-300 max-w-3xl mx-auto">
              7 dias grátis em qualquer plano, sem pedir cartão. Cancele quando quiser, sem multa.
            </p>
          </div>
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-terra-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </section>
    )
  }

  // Debug: verificar se chegou até aqui
  if (!plans || orderedPlans.length === 0) {
    return (
      <section className="py-20 bg-surface dark:bg-ink-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display text-ink-900 dark:text-cream-50 mb-4">
              Comece grátis. <span className="italic font-normal text-terra-500">Continue se fizer sentido.</span>
            </h2>
            <p className="text-xl text-ink-500 dark:text-cream-300 max-w-3xl mx-auto">
              7 dias grátis em qualquer plano, sem pedir cartão. Cancele quando quiser, sem multa.
            </p>
          </div>
          <div className="flex items-center justify-center py-12">
            <p className="text-ink-500 dark:text-ink-300">Nenhum plano encontrado</p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="pricing" className="py-20 bg-surface dark:bg-ink-900">
      <div ref={revealRef} className="container mx-auto px-4">
        <div data-reveal-item className="text-center mb-16">
          <p className="flex items-center justify-center gap-3 font-mono text-caderno-xs uppercase tracking-widest text-ink-400 dark:text-ink-300 mb-4">
            <span className="text-terra-500">06</span>
            <span aria-hidden="true" className="w-8 h-px bg-cream-400 dark:bg-ink-500" />
            Planos
          </p>
          <h2 className="text-3xl md:text-4xl font-display text-ink-900 dark:text-cream-50 mb-4">
            Comece grátis. <span className="italic font-normal text-terra-500">Continue se fizer sentido.</span>
          </h2>
          <p className="text-xl text-ink-500 dark:text-cream-300 max-w-3xl mx-auto mb-8">
            7 dias grátis em qualquer plano, sem pedir cartão. Cancele quando quiser, sem multa.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            <span className={`text-sm font-medium ${!isAnnual ? 'text-ink-900 dark:text-cream-50' : 'text-ink-400 dark:text-ink-300'}`}>
              Mensal
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-terra-500 focus:ring-offset-2 ${
                isAnnual ? 'bg-terra-500' : 'bg-cream-200 dark:bg-ink-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-surface transition-transform ${
                  isAnnual ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${isAnnual ? 'text-ink-900 dark:text-cream-50' : 'text-ink-400 dark:text-ink-300'}`}>
              Anual
            </span>
            {isAnnual && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-moss-100 dark:bg-moss-500/30 text-moss-500 dark:bg-moss-500 dark:text-moss-500">
                Economize até 33%
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {orderedPlans.map((plan, index) => {
            const baseName = plan.name.replace(' Anual', '')
            const isPopular = baseName.toLowerCase() === highlight // destaque controlado pela prop
            const savings = calculateSavings(plan)

            return (
              <div
                key={plan.id}
                data-reveal-item
                style={{ '--reveal-delay': `${index * 120}ms` } as React.CSSProperties}
                className={`relative bg-surface dark:bg-ink-700 rounded-caderno-lg shadow-caderno-lg border-2 transition-all duration-300 hover:shadow-caderno-xl dark:border-ink-500 dark:hover:border-ink-700 ${
                  isPopular
                    ? 'border-terra-500 scale-105'
                    : 'border-cream-400 hover:border-cream-400'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-terra-500 text-cream-50 px-4 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                      <Star className="w-4 h-4" />
                      <span>Mais escolhido</span>
                    </div>
                  </div>
                )}

                <div className="p-8">
                  <div className="text-center mb-8">
                    <div className={`w-16 h-16 rounded-caderno-md flex items-center justify-center mx-auto mb-4 ${
                      baseName === 'Gratuito' ? 'bg-cream-100 dark:bg-ink-700' :
                      baseName === 'Premium' ? 'bg-terra-100 dark:bg-terra-700' :
                      'bg-terra-100 dark:bg-terra-700'
                    }`}>
                      {baseName === 'Gratuito' && <Zap className="w-8 h-8 text-ink-500 dark:text-ink-300" />}
                      {baseName === 'Premium' && <Star className="w-8 h-8 text-terra-500 dark:text-terra-300" />}
                      {baseName === 'Escolar' && <Building className="w-8 h-8 text-terra-700 dark:text-terra-300" />}
                    </div>

                    <h3 className="text-2xl font-display text-ink-900 dark:text-cream-50 mb-2">
                      {baseName}
                      {isAnnual && (
                        <span className="ml-2 text-sm font-normal text-moss-500 dark:text-moss-500">
                          Anual
                        </span>
                      )}
                    </h3>

                    <div className="mb-4">
                      {isAnnual && savings ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-center space-x-2">
                            <span className="text-2xl font-bold text-ink-900 dark:text-cream-50">
                              R$ {parseFloat(plan.price).toFixed(2).replace('.', ',')}
                            </span>
                            <span className="text-ink-500 dark:text-ink-300">/ano</span>
                          </div>
                          <div className="text-sm text-ink-400 dark:text-ink-300">
                            <span className="line-through">
                              R$ {savings.monthlyTotal.toFixed(2).replace('.', ',')}
                            </span>
                            <span className="ml-2 text-moss-500 dark:text-moss-500 font-medium">
                              Economize {savings.percentage}%
                            </span>
                          </div>
                          <div className="text-xs text-ink-400 dark:text-ink-300">
                            R$ {(parseFloat(plan.price) / 12).toFixed(2).replace('.', ',')}/mês
                          </div>
                        </div>
                      ) : (
                        <div>
                          <span className="text-4xl font-bold text-ink-900 dark:text-cream-50">
                            R$ {parseFloat(plan.price).toFixed(2).replace('.', ',')}
                          </span>
                          <span className="text-ink-500 dark:text-ink-300">
                            /{isAnnual ? 'ano' : 'mês'}
                          </span>
                        </div>
                      )}
                    </div>

                    <p className="text-ink-500 dark:text-cream-300">{plan.description}</p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features?.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-moss-500 mt-0.5 flex-shrink-0" />
                        <span className="text-ink-700 dark:text-cream-100">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleSubscribe(plan, baseName)}
                    disabled={loadingPriceId === plan.stripe_price_id}
                    className={`w-full py-3 px-4 rounded-caderno-xs font-medium transition-colors ${
                      isPopular
                        ? 'bg-terra-500 hover:bg-terra-700 text-cream-50 disabled:bg-terra-300'
                        : baseName === 'Escolar'
                          ? 'bg-terra-700 hover:bg-terra-900 text-cream-50 disabled:bg-terra-300'
                          : 'bg-ink-900 hover:bg-ink-700 text-cream-50 disabled:bg-ink-500'
                    } disabled:cursor-not-allowed`}
                  >
                    {loadingPriceId === plan.stripe_price_id ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-cream-50" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processando...
                      </span>
                    ) : (
                      baseName === "Gratuito" ? "Começar Agora" :
                      baseName === "Escolar" ? "Assinar Plano Escolar" :
                      `Assinar ${baseName}`
                    )}
                  </button>
                </div>
              </div>
            )
          })}
          <div
            data-reveal-item
            style={{ '--reveal-delay': `${orderedPlans.length * 120}ms` } as React.CSSProperties}
            className="relative bg-surface dark:bg-ink-700 rounded-caderno-lg shadow-caderno-lg border-2 border-cream-400 dark:border-ink-500 transition-all duration-300 hover:shadow-caderno-xl dark:hover:border-ink-700"
          >
            <CustomPlanCard origem="landing" />
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-ink-500 dark:text-ink-300 mb-4">
            Todas as assinaturas incluem 7 dias de teste gratuito
          </p>
          <p className="text-sm text-ink-400 dark:text-ink-300">
            Cancele a qualquer momento. Sem taxas de cancelamento.
          </p>
        </div>
      </div>
    </section>
  )
}

export default PricingSection